/**
 * scoring.ts — shared slide scoring utilities.
 *
 * IMPORTANT: `getSlideMaxPoints` must stay in sync with the per-slide
 * `maxPts = slide.points ?? <default>` logic in SlidePlayer.tsx.
 * Add a case here whenever a new slide type is added.
 */
import {type Slide} from '#/types/capsule'

/** Non-exercise slide types that never contribute points. */
export const NON_SCORED_TYPES = new Set([
  'video',
  'content',
  'accordion',
  'text_block',
  'image_slide',
  'table_slide',
  'chart_slide',
  'formula_slide',
  'drawing_slide',
])

/**
 * Returns the maximum achievable points for a single slide, mirroring
 * each slide component's actual default scoring logic.
 */
export function getSlideMaxPoints(slide: Slide): number {
  if (slide.points != null) return slide.points
  switch (slide.type) {
    case 'single_choice':
    case 'true_false':
    case 'image_choice':
      return 1
    case 'multiple_choice':
      return Math.max((slide.options ?? []).filter(o => o.isCorrect).length, 1)
    case 'matching':
      return Math.max((slide.pairs ?? []).length, 1)
    case 'ordering':
      return Math.max((slide.orderItems ?? []).length, 1)
    case 'drag_words': {
      const blanks = (slide.dragWordsTemplate ?? '').match(/\[[^\]]+\]/g) ?? []
      return Math.max(blanks.length, 1)
    }
    case 'fill_blank':
      return Math.max((slide.blanks ?? []).length, 1)
    case 'find_words':
      return 20
    case 'mark_words':
      return Math.max((slide.markWordsCorrect ?? []).length, 1)
    case 'complex_fill_blank':
      return Math.max((slide.complexBlanks ?? []).length, 1)
    default:
      return 1
  }
}

/**
 * Computes the total maximum points for all scored slides in a capsule.
 */
export function getCapsuleMaxPoints(slides: Slide[]): number {
  return slides
    .filter(sl => !NON_SCORED_TYPES.has(sl.type))
    .reduce((sum, sl) => sum + getSlideMaxPoints(sl), 0)
}
